/**
 * Studio chrome, derived from the route — W-1.
 *
 * One pure function answers the three questions the shell needs: which nav item
 * is active, what the breadcrumb trail is, and whether this route edits
 * something. Deriving it is the point: the current defect exists because
 * `StudioShell` renders a fixed nav array and no screen tells it anything, so
 * the asset form shows three peers with none of them marked.
 *
 * `1b`'s rule — a child route keeps its **parent's** nav item active — is one
 * line of data here rather than a condition spread across components.
 */

export type NavKey = "assets" | "tiers" | "lists";

export type Crumb = {
  label: string;
  /** Absent on the current page, and on any crumb pointing at where we are. */
  href?: string;
};

export type StudioChrome = {
  /** null on a route with no nav home — nothing is underlined rather than the wrong thing. */
  activeNav: NavKey | null;
  crumbs: Crumb[];
  /** Does this route edit something? Drives whether an action zone exists at all. */
  isEditing: boolean;
};

export const STUDIO_ROOT = "/studio";

const NAV_LABEL: Record<NavKey, string> = {
  assets: "ASSETS",
  tiers: "TIERS & ACCESS",
  lists: "LISTS",
};

/** Trailing slashes and empty segments would otherwise make `/studio/` unknown. */
const segmentsOf = (pathname: string): string[] =>
  pathname.split("/").filter(Boolean);

/**
 * @param pathname a Studio pathname
 * @param recordLabel the title of the record being edited, when there is one.
 *   Passed in rather than fetched: this module stays pure, and the screen that
 *   already holds the asset is the only thing that knows its title (FR-4).
 */
export function studioChrome(
  pathname: string,
  recordLabel?: string
): StudioChrome {
  const segments = segmentsOf(pathname);

  // Anything outside Studio is not this module's business, and guessing would
  // be worse than admitting it.
  if (segments[0] !== "studio") {
    return { activeNav: null, crumbs: [], isEditing: false };
  }

  const root: Crumb = { label: "STUDIO", href: STUDIO_ROOT };
  const section = segments[1];

  // `/studio` is the assets list. ASSETS is therefore both the section and the
  // root, and both crumbs point at the same place - which `withoutSelfLinks`
  // resolves rather than this function pretending otherwise.
  if (section === undefined) {
    return {
      activeNav: "assets",
      crumbs: withoutSelfLinks([root, { label: "ASSETS", href: STUDIO_ROOT }], pathname),
      // The list is not an editing screen. It must never grow an action zone.
      isEditing: false,
    };
  }

  if (section === "tiers" || section === "lists") {
    return {
      activeNav: section,
      crumbs: withoutSelfLinks(
        [root, { label: NAV_LABEL[section], href: pathname }],
        pathname
      ),
      // Both edit the creator record, so both carry the same action zone as
      // everything else (FR-12).
      isEditing: true,
    };
  }

  if (section === "assets") {
    const id = segments[2];
    if (id === undefined) {
      // `/studio/assets` is not a route today, but a stray link to it should
      // land on the list rather than on nothing.
      return {
        activeNav: "assets",
        crumbs: withoutSelfLinks([root, { label: "ASSETS", href: STUDIO_ROOT }], pathname),
        isEditing: false,
      };
    }
    return {
      // The rule that has never worked: a child highlights its parent (FR-1).
      activeNav: "assets",
      crumbs: withoutSelfLinks(
        [
          root,
          { label: "ASSETS", href: STUDIO_ROOT },
          // Always "ASSET", never the type's heading noun (FR-6, D-33): the
          // type is chosen inside the form, so the title cannot know it here.
          {
            label: id === "new" ? "NEW ASSET" : labelFor(recordLabel),
            href: pathname,
          },
        ],
        pathname
      ),
      isEditing: true,
    };
  }

  // An unknown Studio route degrades to the root rather than throwing, and
  // underlines nothing rather than underlining a guess.
  return {
    activeNav: null,
    crumbs: withoutSelfLinks([root], pathname),
    isEditing: false,
  };
}

/** An untitled asset is still a place; it just has no name yet (FR-4). */
const labelFor = (recordLabel?: string): string => {
  const trimmed = recordLabel?.trim();
  return trimmed ? trimmed.toUpperCase() : "UNTITLED";
};

/**
 * Drops the href from any crumb pointing at the page already on screen.
 *
 * **One rule, not two.** An earlier version also stripped the last crumb by
 * position, which read as defensive and was in fact dead: the last crumb *is*
 * the current page, so the position test never fired without this one also
 * firing. Breaking it changed no test, which is how it was found. A branch that
 * cannot be observed is not a safety net, it is a claim nobody checks.
 *
 * It also does real work in the middle of a trail: at `/studio` the root and the
 * section are the same URL, and two links back to where you are standing is how
 * a trail stops meaning depth.
 */
function withoutSelfLinks(crumbs: Crumb[], pathname: string): Crumb[] {
  const here = "/" + segmentsOf(pathname).join("/");
  return crumbs.map((crumb) => {
    if (crumb.href === here) {
      const { href: _dropped, ...rest } = crumb;
      return rest;
    }
    return crumb;
  });
}
